import { Alert, Avatar, Descriptions, Drawer, Space, Spin, Tag, Typography, theme } from 'antd';
import dayjs from 'dayjs';
import { Children, type ReactNode, isValidElement, useEffect, useState } from 'react';
import { http } from '../../api/client.js';

export interface AdminUserDetail {
  systemuserid: string;
  fullname: string | null;
  domainname: string | null;
  internalemailaddress: string | null;
  employee_number: string | null;
  avatar_url: string | null;
  jobtitle: string | null;
  mobilephone: string | null;
  workplace: string | null;
  hired_at: string | null;
  gender: string | null;
  dingtalk_state_code: string | null;
  dingtalk_hide_mobile: boolean | null;
  dingtalk_telephone: string | null;
  dingtalk_email: string | null;
  dingtalk_org_email: string | null;
  dingtalk_remark: string | null;
  dingtalk_extension: unknown;
  dingtalk_active: boolean | null;
  dingtalk_admin: boolean | null;
  dingtalk_boss: boolean | null;
  dingtalk_leader: boolean | null;
  dingtalk_disable_status: boolean | null;
  dingtalk_exclusive_account: boolean | null;
  dingtalk_login_id: string | null;
  dingtalk_exclusive_account_type: string | null;
  dingtalk_nickname: string | null;
  dingtalk_exclusive_account_corp_name: string | null;
  dingtalk_exclusive_account_corp_id: string | null;
  dingtalk_user_id: string | null;
  dingtalk_union_id: string | null;
  dingtalk_open_id: string | null;
  dingtalk_corp_id: string | null;
  dingtalk_department_ids: string[];
  dingtalk_primary_department_id: string | null;
  dingtalk_sync_state: string | null;
  dingtalk_last_synced_at: string | null;
  isdisabled: boolean | null;
  businessunitid: string | null;
  businessunit: { name: string | null } | null;
  businessunits: Array<{
    businessunitid: string;
    name: string | null;
    dingtalkDepartmentId: string | null;
    dingtalkSortOrder: number | null;
    source: string;
  }>;
  roles: Array<{ roleid: string; name: string; roleType: string }>;
  createdon: string | null;
  modifiedon: string | null;
}

interface Props {
  userId: string | null;
  onClose: () => void;
}

export function AdminUserDetailDrawer({ userId, onClose }: Props) {
  const { token } = theme.useToken();
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!userId) {
      setDetail(null);
      setError(false);
      return;
    }
    let active = true;
    setDetail(null);
    setLoading(true);
    setError(false);
    http
      .get<AdminUserDetail>(`/admin/users/${userId}`)
      .then((response) => {
        if (active) setDetail(response.data);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [userId]);

  return (
    <Drawer
      title="用户详情"
      open={userId !== null}
      onClose={onClose}
      width="min(880px, 100vw)"
      destroyOnClose
    >
      <Spin spinning={loading}>
        {error ? (
          <Alert type="error" showIcon message="用户详情加载失败" description="请稍后重试。" />
        ) : detail ? (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Space size={14} align="center">
              <Avatar size={64} src={detail.avatar_url}>
                {(detail.fullname ?? detail.domainname ?? '?').slice(0, 1)}
              </Avatar>
              <Space direction="vertical" size={2}>
                <Space wrap>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    {detail.fullname ?? '未命名用户'}
                  </Typography.Title>
                  <Tag color={detail.isdisabled ? 'default' : 'green'}>
                    {detail.isdisabled ? '已禁用' : '已启用'}
                  </Tag>
                  {detail.dingtalk_user_id && <Tag color="blue">钉钉用户</Tag>}
                </Space>
                <Typography.Text type="secondary">
                  {[detail.jobtitle, detail.workplace].filter(Boolean).join(' · ') || '暂无职位信息'}
                </Typography.Text>
              </Space>
            </Space>

            <DetailGroup title="基础信息">
              <Item label="姓名" value={detail.fullname} />
              <Item label="钉钉昵称" value={detail.dingtalk_nickname} />
              <Item label="登录名" value={detail.domainname} copyable />
              <Item label="工号" value={detail.employee_number} copyable />
              <Item label="职位" value={detail.jobtitle} />
              <Item label="工作地点" value={detail.workplace} />
              <Item label="性别" value={detail.gender} />
              <Item label="入职时间" value={formatDate(detail.hired_at)} />
              <Item label="手机" value={detail.mobilephone} copyable />
              <Item label="办公电话" value={detail.dingtalk_telephone} copyable />
              <Item label="平台邮箱" value={detail.internalemailaddress} copyable />
              <Item label="钉钉邮箱" value={detail.dingtalk_email} copyable />
              <Item label="企业邮箱" value={detail.dingtalk_org_email} copyable />
              <Item label="国家/地区码" value={detail.dingtalk_state_code} />
              <Item label="备注" value={detail.dingtalk_remark} span={2} />
            </DetailGroup>

            <DetailGroup title="组织与角色">
              <Descriptions.Item label="所属组织" span={2}>
                {detail.businessunits.length ? (
                  <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    {detail.businessunits.map((unit) => (
                      <Space key={unit.businessunitid} wrap>
                        <Tag color="cyan">{unit.name ?? unit.businessunitid}</Tag>
                        <Typography.Text type="secondary">
                          部门 ID：{unit.dingtalkDepartmentId ?? '—'}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                          排序：{unit.dingtalkSortOrder ?? '—'}
                        </Typography.Text>
                        <Typography.Text type="secondary">来源：{unit.source}</Typography.Text>
                      </Space>
                    ))}
                  </Space>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Item label="主部门 ID" value={detail.dingtalk_primary_department_id} copyable />
              <Item
                label="全部部门 ID"
                value={detail.dingtalk_department_ids?.join('、') || null}
                copyable
              />
              <Descriptions.Item label="平台角色" span={2}>
                {detail.roles.length ? (
                  <Space size={[4, 4]} wrap>
                    {detail.roles.map((role) => (
                      <Tag key={role.roleid} color="blue">
                        {role.name}（{role.roleType}）
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
            </DetailGroup>

            <DetailGroup title="钉钉账号与同步状态">
              <Item label="User ID" value={detail.dingtalk_user_id} copyable />
              <Item label="Union ID" value={detail.dingtalk_union_id} copyable />
              <Item label="Open ID" value={detail.dingtalk_open_id} copyable />
              <Item label="Corp ID" value={detail.dingtalk_corp_id} copyable />
              <Item label="钉钉登录 ID" value={detail.dingtalk_login_id} copyable />
              <Item label="同步状态" value={detail.dingtalk_sync_state} />
              <Item label="最后同步时间" value={formatDate(detail.dingtalk_last_synced_at)} />
              <Item label="隐藏手机号" value={booleanValue(detail.dingtalk_hide_mobile)} />
              <Item label="账号已激活" value={booleanValue(detail.dingtalk_active)} />
              <Item label="管理员" value={booleanValue(detail.dingtalk_admin)} />
              <Item label="企业负责人" value={booleanValue(detail.dingtalk_boss)} />
              <Item label="部门主管" value={booleanValue(detail.dingtalk_leader)} />
              <Item label="钉钉禁用状态" value={booleanValue(detail.dingtalk_disable_status)} />
              <Item label="专属账号" value={booleanValue(detail.dingtalk_exclusive_account)} />
              <Item label="专属账号类型" value={detail.dingtalk_exclusive_account_type} />
              <Item label="专属账号企业" value={detail.dingtalk_exclusive_account_corp_name} />
              <Item
                label="专属账号企业 ID"
                value={detail.dingtalk_exclusive_account_corp_id}
                copyable
                span={2}
              />
            </DetailGroup>

            <DetailGroup title="平台记录">
              <Item label="平台用户 ID" value={detail.systemuserid} copyable span={2} />
              <Item label="创建时间" value={formatDate(detail.createdon)} />
              <Item label="修改时间" value={formatDate(detail.modifiedon)} />
            </DetailGroup>

            <section>
              <Typography.Title level={5}>钉钉扩展属性</Typography.Title>
              <Typography.Paragraph copyable={detail.dingtalk_extension != null} style={{ margin: 0 }}>
                <pre
                  style={{
                    margin: 0,
                    padding: 12,
                    overflow: 'auto',
                    borderRadius: token.borderRadius,
                    background: token.colorFillQuaternary,
                  }}
                >
                  {formatExtension(detail.dingtalk_extension)}
                </pre>
              </Typography.Paragraph>
            </section>
          </Space>
        ) : null}
      </Spin>
    </Drawer>
  );
}

function DetailGroup({ title, children }: { title: string; children: ReactNode }) {
  const items = Children.map(children, (child) => {
    if (isValidElement<DetailItemProps>(child) && child.type === Item) {
      return renderItem(child.props);
    }
    return child;
  });
  return (
    <section>
      <Typography.Title level={5}>{title}</Typography.Title>
      <Descriptions bordered size="small" column={2}>
        {items}
      </Descriptions>
    </section>
  );
}

interface DetailItemProps {
  label: string;
  value: string | number | null | undefined;
  copyable?: boolean;
  span?: number;
}

function Item(_props: DetailItemProps) {
  return null;
}

function renderItem({ label, value, copyable = false, span }: DetailItemProps) {
  const shown = value === null || value === undefined || value === '' ? '—' : String(value);
  return (
    <Descriptions.Item key={label} label={label} span={span}>
      <Typography.Text copyable={copyable && shown !== '—'}>{shown}</Typography.Text>
    </Descriptions.Item>
  );
}

function booleanValue(value: boolean | null): string {
  if (value === null) return '—';
  return value ? '是' : '否';
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : value;
}

export function formatExtension(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}
